/**
 * GameLoop — fixed-timestep simulation with decoupled, interpolated rendering.
 *
 * Why fixed-step: the boids/flocking integrator and the incremental economy
 * must be deterministic and frame-rate independent. A phone at 60fps and a
 * laptop at 144fps must simulate identically, so we advance the simulation in
 * fixed SIM_DT chunks and render with an interpolation `alpha` for smoothness.
 *
 * Pattern: Glenn Fiedler's "Fix Your Timestep" accumulator.
 *
 *   update(dt)  -> advance simulation by exactly SIM_DT seconds
 *   render(alpha) -> draw, where alpha in [0,1) blends prev->current state
 *
 * The loop also survives background tabs (mobile lock screens) by clamping the
 * frame delta so we never try to catch up on 10 minutes of missed time.
 */
export interface GameLoopCallbacks {
  /** Advance the simulation by a fixed `dt` seconds. */
  update: (dt: number) => void;
  /** Render; `alpha` in [0,1) is how far we are into the next sim step. */
  render: (alpha: number) => void;
  /** Optional: coarse per-second tick for UI/economy readouts. */
  onSecond?: (stats: LoopStats) => void;
}

export interface LoopStats {
  fps: number;
  simStepsPerFrame: number;
  simTimeMs: number;
}

/**
 * 60 Hz simulation so entity motion matches a 60 Hz display frame-for-frame
 * (30 Hz looked stuttery when the mothership moved). Still cheap at this scale;
 * if we later need to decouple further, render with the `alpha` interpolation.
 */
export const SIM_HZ = 60;
export const SIM_DT = 1 / SIM_HZ;
/** Never advance more than this many sim steps in one frame (spiral-of-death guard). */
const MAX_STEPS_PER_FRAME = 6;

export class GameLoop {
  private cbs: GameLoopCallbacks;
  private running = false;
  private rafId = 0;

  private accumulator = 0;
  private lastTime = 0;

  // rolling stats
  private frames = 0;
  private secondTimer = 0;
  private lastSimSteps = 0;
  private lastSimTimeMs = 0;

  constructor(cbs: GameLoopCallbacks) {
    this.cbs = cbs;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  private frame = (now: number): void => {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.frame);

    // Seconds since last frame, clamped (tab was backgrounded / phone locked).
    let frameTime = (now - this.lastTime) / 1000;
    this.lastTime = now;
    if (frameTime > 0.25) frameTime = 0.25; // cap catch-up to 250ms

    this.accumulator += frameTime;

    const simStart = performance.now();
    let steps = 0;
    while (this.accumulator >= SIM_DT && steps < MAX_STEPS_PER_FRAME) {
      this.cbs.update(SIM_DT);
      this.accumulator -= SIM_DT;
      steps++;
    }
    // If we hit the step cap, drop the backlog rather than spiral.
    if (steps >= MAX_STEPS_PER_FRAME) this.accumulator = 0;
    this.lastSimSteps = steps;
    this.lastSimTimeMs = performance.now() - simStart;

    // Interpolation factor for rendering between the last two sim states.
    const alpha = this.accumulator / SIM_DT;
    this.cbs.render(alpha);

    // stats bookkeeping
    this.frames++;
    this.secondTimer += frameTime;
    if (this.secondTimer >= 1) {
      this.cbs.onSecond?.({
        fps: this.frames / this.secondTimer,
        simStepsPerFrame: this.lastSimSteps,
        simTimeMs: this.lastSimTimeMs,
      });
      this.frames = 0;
      this.secondTimer = 0;
    }
  };
}
